'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { getViolationHistory, type Building } from '@repo/api';
import { ViolationHistorySection } from './ViolationHistorySection';
import { ViolationHistorySectionSkeleton } from './ViolationHistorySectionSkeleton';
import { useBuilding } from '../../context/BuildingProvider';
import { PLACEHOLDER_REPORT_DATA } from '../../context/constants';
import useIntersectionObserver from '@/hooks/useIntersectionObserver';
import { useAnalytics } from '@/hooks/useAnalytics';

const fetchCache = new Map<string, { timestamp: number; isFetching: boolean; data?: any }>();

interface ViolationHistoryProps {
  building: Building;
  buildingAddress: string;

  showSkeleton: boolean; // parent (explorer/addReport flow)
  isReportReady: boolean; // true only when addReport done or hasAccess
  shouldBlur?: boolean; // if you still need it elsewhere
}

function ViolationHistory({
  building,
  buildingAddress,
  showSkeleton,
  isReportReady,
}: ViolationHistoryProps) {
  const analytics = useAnalytics();
  const sectionRef = useRef<HTMLDivElement>(null);
  const onScreen = useIntersectionObserver<HTMLDivElement>(
    sectionRef,
    useMemo(() => ({ threshold: 0.5 }), []),
  );

  const [baseViolationData, setBaseViolationData] = useState<any | null>(null);
  const [baseLoading, setBaseLoading] = useState(true);
  const [baseError, setBaseError] = useState<string | null>(null);

  useEffect(() => {
    let timer: number | undefined;
    if (onScreen) {
      timer = window.setTimeout(() => {
        analytics.track('rentalreport_violationhistory_scroll');
      }, 5000);
    }
    return () => {
      if (timer) window.clearTimeout(timer);
    };
  }, [analytics, onScreen]);

  // reset when building change

  useEffect(() => {
    // keep skeleton up until addReport is ready
    if (!isReportReady) {
      setBaseLoading(false);
      setBaseViolationData(PLACEHOLDER_REPORT_DATA);
      return;
    }

    (async () => {
      setBaseLoading(true);
      setBaseError(null);

      try {
        const response = await getViolationHistory({ buildingId: building.id });
        setBaseViolationData(response);
      } catch (err: any) {
        setBaseError(err?.message || 'Failed to load violation history');
        // allow retry if you want: didFetchRef.current = false;
      } finally {
        setBaseLoading(false);
      }
    })();
  }, [isReportReady]);

  // ✅ Single skeleton condition
  const shouldShowSkeleton = showSkeleton || baseLoading;

  if (shouldShowSkeleton) {
    return <ViolationHistorySectionSkeleton />;
  }

  return (
    <div ref={sectionRef}>
      <ViolationHistorySection
        buildingId={building.id}
        buildingSlug={building.slug}
        buildingAddress={buildingAddress}
        isBlurRequired={!isReportReady}
        baseViolationData={baseViolationData}
        baseLoading={baseLoading}
        baseError={baseError}
      />
    </div>
  );
}

export default ViolationHistory;
